import * as XLSX from "xlsx";

import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { Holiday } from "~/shared/schemas/holiday-schema.ts";
import { isHoliday } from "~/shared/utils/date-helpers.ts";

import {
	BULK_UPLOAD_TEMPLATE_PATHS,
	ERROR_OFFSET,
	EXCEL_DATE_OFFSET,
	INSURANCE_APPLY_TYPE_ALL_CARRIERS,
	INSURANCE_PRODUCT_CONDITION_FIELD,
	INSURANCE_PRODUCT_CONDITIONS,
	INSURANCE_REQUESTED_FIELD,
	INSURANCE_VALUE_FIELD,
	MAX_ERROR_DISPLAY,
	MAX_SHIPMENT_ITEMS,
	MS_IN_DAY,
	REQUIRED_FIELDS,
	SERVICE_KEYS,
	TENDER_INSURANCE_FIELDS,
	TENDER_REQUIRED_FIELDS,
	TENDER_REQUIRED_HEADERS,
} from "../constants/bulk-upload-constants.ts";
import type { BulkUploadTemplateMetadata } from "../schemas/bulk-upload-schema.ts";
import { bulkUploadTemplateMetadataSchema } from "../schemas/bulk-upload-schema.ts";
import type {
	BulkUploadQuote,
	BulkUploadQuoteCommodity,
} from "../types/bulk-upload-types";
import type {
	BulkUploadTemplateType,
	PageData,
	RowValue,
} from "../types/bulk-upload-types.ts";

type BulkUploadLookupData = Pick<
	PageData,
	"unitTypes" | "freightClassData" | "hazmatClasses" | "hazmatGroups"
>;

export const downloadTemplate = (
	baseUrl: string,
	templateType: BulkUploadTemplateType = "quote",
) => {
	const link = document.createElement("a");
	const templatePath = BULK_UPLOAD_TEMPLATE_PATHS[templateType];
	link.href = `${baseUrl}${templatePath}`;
	link.download = templatePath.split("/").at(-1) ?? "bulk-upload-template.xlsx";
	link.click();
};

export const cleanZipCodeString = (
	value: string | undefined | null,
): string => {
	if (!value) return "";
	const trimmed = value.trim();
	if (/^[\d\s-]+$/.test(trimmed)) {
		const digits = trimmed.replaceAll(/\D/g, "");
		if (digits.length === 5) return digits;
		if (digits.length === 9) return digits.slice(0, 5);
		return "";
	}
	const cleaned = trimmed.replaceAll(" ", "").toUpperCase();
	return cleaned.length === 6
		? cleaned.slice(0, 3) + " " + cleaned.slice(3)
		: "";
};

export const normalizeBulkUploadHeader = (header: string): string => {
	return header.trim().replace(/^Item\s+([1-5])\s+/, "Item$1 ");
};

export const normalizeBulkUploadHeaders = (headers: string[]): string[] =>
	headers.map((header) => normalizeBulkUploadHeader(header));

export const isBlankBulkUploadRow = (row: RowValue[]): boolean =>
	!row.some(
		(cell) =>
			cell !== null && cell !== undefined && cell.toString().trim().length > 0,
	);

export const trimTrailingBlankBulkUploadRows = (
	rows: RowValue[][],
): RowValue[][] => {
	let end = rows.length;
	while (end > 0 && isBlankBulkUploadRow(rows[end - 1] ?? [])) {
		end--;
	}
	return rows.slice(0, end);
};

// Compatibility inference for the metadata-less production Quote workbook.
export const detectBulkUploadTemplateType = (
	sheetName: string,
	headers: string[],
): BulkUploadTemplateType => {
	const normalizedSheetName = sheetName.trim().toLowerCase();
	const normalizedHeaders = normalizeBulkUploadHeaders(headers);
	const hasTenderOnlyHeaders =
		normalizedHeaders.includes("Emergency Contact Name") &&
		normalizedHeaders.includes("Emergency Contact Phone");

	return normalizedSheetName.startsWith("tender") || hasTenderOnlyHeaders
		? "tender"
		: "quote";
};

const LEGACY_QUOTE_HEADER_ROW = 2;
const LEGACY_QUOTE_DATA_START_ROW = 4;
const TEMPLATE_METADATA_SHEET = "_TRAX";

export const INVALID_BULK_UPLOAD_TEMPLATE_MESSAGE =
	"This upload template version is invalid or unsupported. Download the latest template and try again.";
export const MISSING_TENDER_TEMPLATE_METADATA_MESSAGE =
	"This Tender template is missing required metadata. Download the latest Tender Template and try again.";

export type BulkUploadWorkbookLayout = Omit<
	BulkUploadTemplateMetadata,
	"templateVersion"
> & {
	templateVersion: BulkUploadTemplateMetadata["templateVersion"] | null;
	isLegacyQuote: boolean;
};

export type BulkUploadWorkbookLayoutResult =
	| { success: true; data: BulkUploadWorkbookLayout }
	| { success: false; error: string };

const readTemplateMetadata = (
	workbook: XLSX.WorkBook,
): BulkUploadTemplateMetadata | null => {
	const metadataSheet = workbook.Sheets[TEMPLATE_METADATA_SHEET];
	if (!metadataSheet) return null;

	const metadataRows = XLSX.utils.sheet_to_json<RowValue[]>(metadataSheet, {
		defval: "",
		header: 1,
		raw: true,
	});
	const metadataRecord = Object.fromEntries(
		metadataRows
			.filter((row) => String(row[0] ?? "").trim().length > 0)
			.map((row) => [String(row[0]).trim(), row[1]]),
	);
	const parsed = bulkUploadTemplateMetadataSchema.safeParse(metadataRecord);
	return parsed.success ? parsed.data : null;
};

export const resolveBulkUploadWorkbookLayout = (
	workbook: XLSX.WorkBook,
): BulkUploadWorkbookLayoutResult => {
	const sheetName = workbook.SheetNames[0];
	const worksheet = sheetName ? workbook.Sheets[sheetName] : undefined;
	if (!sheetName || !worksheet) {
		return { success: false, error: INVALID_BULK_UPLOAD_TEMPLATE_MESSAGE };
	}

	if (workbook.Sheets[TEMPLATE_METADATA_SHEET]) {
		const metadata = readTemplateMetadata(workbook);
		return metadata
			? { success: true, data: { ...metadata, isLegacyQuote: false } }
			: { success: false, error: INVALID_BULK_UPLOAD_TEMPLATE_MESSAGE };
	}

	const rows = XLSX.utils.sheet_to_json<RowValue[]>(worksheet, { header: 1 });
	const headersRaw = rows[LEGACY_QUOTE_HEADER_ROW - 1];
	const headers = Array.isArray(headersRaw) ? headersRaw.map(String) : [];
	if (detectBulkUploadTemplateType(sheetName, headers) === "tender") {
		return {
			success: false,
			error: MISSING_TENDER_TEMPLATE_METADATA_MESSAGE,
		};
	}

	return {
		success: true,
		data: {
			templateType: "quote",
			templateVersion: null,
			headerRow: LEGACY_QUOTE_HEADER_ROW,
			dataStartRow: LEGACY_QUOTE_DATA_START_ROW,
			isLegacyQuote: true,
		},
	};
};

export const parseExcelValue = (
	value: unknown,
	type: "date" | "time",
): string | null => {
	if (type === "time" && typeof value === "string") {
		const timeMatch = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?\s*([AP]M)$/i.exec(
			value.trim(),
		);
		if (timeMatch) {
			const hours = Number(timeMatch[1]);
			if (hours < 1 || hours > 12) return null;
			return `${hours}:${timeMatch[2]} ${timeMatch[3]?.toUpperCase()}`;
		}
	}

	const num = Number.parseFloat(value as string);
	if (Number.isNaN(num)) return null;

	if (type === "date") {
		const date = new Date((num - EXCEL_DATE_OFFSET) * MS_IN_DAY);
		date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
		return date.toISOString();
	}
	if (type === "time") {
		const totalMinutes = Math.round(num * 1440);
		let hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const ampm = hours >= 12 ? "PM" : "AM";
		hours = hours % 12 || 12;
		return hours + ":" + minutes.toString().padStart(2, "0") + " " + ampm;
	}
	return null;
};

export const validateNmfc = (val: string | undefined | null): string | null => {
	if (!val) return "";
	const trimmed = val.trim();
	if (trimmed === "") return "";
	if (trimmed.length > 9) return null;
	return /^[0-9-]+$/.test(trimmed) ? trimmed : null;
};

export const findLookupId = (
	name: string,
	list: { id: number; name: string }[],
) => {
	return (
		list.find((item) => item.name.toLowerCase() === name.toLowerCase())?.id ??
		null
	);
};

export const findLookupIdByNameOrCode = (
	value: string,
	list: { id: number; name: string; code?: string }[],
) => {
	const normalizedValue = value.trim().toLowerCase();
	const lookupValue = normalizedValue === "n/a" ? "none" : normalizedValue;
	return (
		list.find(
			(item) =>
				item.name.toLowerCase() === lookupValue ||
				item.code?.toLowerCase() === lookupValue,
		)?.id ?? null
	);
};

export const validateRowNmfc = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	for (let i = 1; i <= MAX_SHIPMENT_ITEMS; i++) {
		const nmfcValue = rowData["Item" + i + " NMFC"];
		if (nmfcValue && validateNmfc(nmfcValue) === null) {
			pushError(
				"Row " +
					(rowIndex + ERROR_OFFSET) +
					": Invalid value for Item" +
					i +
					" NMFC, can only be numbers and -'s.",
			);
		}
	}
};

export const validateRowLocTypes = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	if (
		rowData["Shipper Location Type"]?.toLowerCase() === "residential" &&
		rowData["Delivery Location Type"]?.toLowerCase() === "residential"
	) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Pickup and Delivery location types cannot both be residential.",
		);
	}
};

export const validateRowRequiredFields = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
	templateType: BulkUploadTemplateType = "quote",
) => {
	const requiredFields =
		templateType === "tender" ? TENDER_REQUIRED_FIELDS : REQUIRED_FIELDS;
	for (const field of requiredFields) {
		if (!rowData[field]) {
			pushError(
				"Row " + (rowIndex + ERROR_OFFSET) + ": Missing value for " + field,
			);
		}
	}

	if (
		templateType === "tender" &&
		(rowData["Shipper Name"]?.length ?? 0) > 50
	) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Shipper Name cannot exceed 50 characters.`,
		);
	}
	if (
		templateType === "tender" &&
		(rowData["Receiver Name"]?.length ?? 0) > 50
	) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Receiver Name cannot exceed 50 characters.`,
		);
	}
};

export const validateRowCaseCount = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	for (let i = 1; i <= MAX_SHIPMENT_ITEMS; i++) {
		const itemQuantity = rowData["Item" + i + " Quantity"];
		if (!itemQuantity) continue;
		if (
			rowData["Sort and Segregate"]?.trim().toLowerCase() === "y" &&
			(!rowData["Item" + i + " Case Count"]?.trim() ||
				!Number.isInteger(Number(rowData["Item" + i + " Case Count"])) ||
				Number(rowData["Item" + i + " Case Count"]) <= 0)
		) {
			pushError(
				"Row " +
					(rowIndex + ERROR_OFFSET) +
					": Sort and Segregate selected and no Piece/Case Count Provided for item " +
					i +
					".",
			);
		}
	}
};

export const validateRowHazmatFlags = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	for (let item = 1; item <= MAX_SHIPMENT_ITEMS; item++) {
		const value = rowData[`Item${item} Is Hazmat`]?.trim();
		if (!value) continue;

		const normalizedValue = value.toLowerCase();
		if (normalizedValue !== "y" && normalizedValue !== "n") {
			pushError(
				`Row ${rowIndex + ERROR_OFFSET}: Item${item} Is Hazmat must be Y or N.`,
			);
		}
	}
};

export const parseInsuranceRequested = (
	value: string | undefined | null,
): boolean | null => {
	const normalizedValue = (value ?? "").trim().toLowerCase();
	if (!normalizedValue) return false;
	if (normalizedValue === "y") return true;
	if (normalizedValue === "n") return false;
	return null;
};

export const parseInsuranceValue = (
	value: string | undefined | null,
): number | null => {
	// Users paste currency-formatted values, so strip symbols, separators and a
	// trailing USD label before parsing.
	const normalizedValue = (value ?? "")
		.trim()
		.replace(/usd$/i, "")
		.replaceAll(/[$,\s]/g, "");
	if (!normalizedValue) return null;
	const parsedValue = Number(normalizedValue);
	return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const findInsuranceProductTypeId = (
	value: string | undefined | null,
): number | null =>
	findLookupId((value ?? "").trim(), INSURANCE_PRODUCT_CONDITIONS);

/**
 * Insurance details are only meaningful when coverage is requested: a row that
 * sets `Insurance Requested` to Y must supply a positive insured value and a
 * supported item condition, so the quote is never tendered with an incomplete
 * coverage request. Rows that leave the column blank or set it to N skip the
 * detail checks entirely and keep whatever the user typed out of the errors.
 */
export const validateRowInsurance = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	const requestedRaw = rowData[INSURANCE_REQUESTED_FIELD]?.trim() ?? "";
	const isRequested = parseInsuranceRequested(requestedRaw);

	if (isRequested === null) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: ${INSURANCE_REQUESTED_FIELD} must be Y or N.`,
		);
		return;
	}

	if (!isRequested) return;

	const shipmentValueRaw = rowData[INSURANCE_VALUE_FIELD]?.trim() ?? "";
	if (shipmentValueRaw === "") {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: ${INSURANCE_VALUE_FIELD} is required when ${INSURANCE_REQUESTED_FIELD} is Y.`,
		);
	} else {
		const shipmentValue = parseInsuranceValue(shipmentValueRaw);
		if (shipmentValue === null || shipmentValue <= 0) {
			pushError(
				`Row ${rowIndex + ERROR_OFFSET}: ${INSURANCE_VALUE_FIELD} must be a number greater than 0.`,
			);
		}
	}

	const productConditionRaw =
		rowData[INSURANCE_PRODUCT_CONDITION_FIELD]?.trim() ?? "";
	if (productConditionRaw === "") {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: ${INSURANCE_PRODUCT_CONDITION_FIELD} is required when ${INSURANCE_REQUESTED_FIELD} is Y.`,
		);
	} else if (findInsuranceProductTypeId(productConditionRaw) === null) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Invalid value for ${INSURANCE_PRODUCT_CONDITION_FIELD}, must be New or Used.`,
		);
	}
};

export const validateRowHazmatContact = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	const hasHazmat = Array.from(
		{ length: MAX_SHIPMENT_ITEMS },
		(_, index) => rowData[`Item${index + 1} Is Hazmat`]?.toLowerCase() === "y",
	).some(Boolean);
	if (!hasHazmat) return;

	if (!rowData["Emergency Contact Name"]?.trim()) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Emergency Contact Name is required for hazmat shipments.`,
		);
	}

	const phoneDigits = (rowData["Emergency Contact Phone"] ?? "").replaceAll(
		/\D/g,
		"",
	);
	if (phoneDigits.length !== 10) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Emergency Contact Phone must contain 10 digits for hazmat shipments.`,
		);
	}
};

export const validateRowTenderHazmatDetails = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	for (let item = 1; item <= MAX_SHIPMENT_ITEMS; item++) {
		if (rowData[`Item${item} Is Hazmat`]?.toLowerCase() !== "y") continue;

		const requiredFields = [
			`Item${item} Case Count`,
			`Item${item} NMFC`,
			`Item${item} UN Number`,
			`Item${item} Proper Shipping Name`,
			`Item${item} Hazmat Class`,
			`Item${item} Packing Group`,
		];
		for (const field of requiredFields) {
			if (!rowData[field]?.trim()) {
				pushError(
					`Row ${rowIndex + ERROR_OFFSET}: ${field} is required for hazmat items.`,
				);
			}
		}

		const pieceCount = Number.parseInt(
			rowData[`Item${item} Case Count`] ?? "",
			10,
		);
		if (Number.isFinite(pieceCount) && pieceCount <= 0) {
			pushError(
				`Row ${rowIndex + ERROR_OFFSET}: Item${item} Case Count must be greater than 0 for hazmat items.`,
			);
		}
	}
};

const validateItemHazmatLookupValues = (
	rowData: Record<string, string>,
	rowIndex: number,
	item: number,
	pushError: (msg: string) => void,
	pageData: BulkUploadLookupData,
) => {
	if (rowData[`Item${item} Is Hazmat`]?.toLowerCase() !== "y") return;

	const hazmatClass = rowData[`Item${item} Hazmat Class`]?.trim();
	if (
		hazmatClass &&
		findLookupIdByNameOrCode(hazmatClass, pageData.hazmatClasses ?? []) === null
	) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Invalid value for Item${item} Hazmat Class.`,
		);
	}

	const packingGroup = rowData[`Item${item} Packing Group`]?.trim();
	if (
		packingGroup &&
		findLookupIdByNameOrCode(packingGroup, pageData.hazmatGroups ?? []) === null
	) {
		pushError(
			`Row ${rowIndex + ERROR_OFFSET}: Invalid value for Item${item} Packing Group.`,
		);
	}
};

export const validateRowLookupValues = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
	pageData?: BulkUploadLookupData | null,
) => {
	if (!pageData) return;

	for (let i = 1; i <= MAX_SHIPMENT_ITEMS; i++) {
		const itemDescription = rowData["Item" + i + " Description"];
		if (!itemDescription) continue;

		const itemUnitType = rowData["Item" + i + " Unit Type"] ?? "";
		if (findLookupId(itemUnitType, pageData.unitTypes) === null) {
			pushError(
				"Row " +
					(rowIndex + ERROR_OFFSET) +
					": Invalid value for Item" +
					i +
					" Unit Type.",
			);
		}

		const itemFreightClass = rowData["Item" + i + " Freight Class"] ?? "";
		if (
			findLookupId("Class " + itemFreightClass, pageData.freightClassData) ===
			null
		) {
			pushError(
				"Row " +
					(rowIndex + ERROR_OFFSET) +
					": Invalid value for Item" +
					i +
					" Freight Class.",
			);
		}

		validateItemHazmatLookupValues(rowData, rowIndex, i, pushError, pageData);
	}
};

export const validateRowTimes = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	const timeFields = [
		"Shipper Start Hours",
		"Shipper End Hours",
		"Receiver Start Hours",
		"Receiver End Hours",
	];

	for (const timeField of timeFields) {
		const rawTime = rowData[timeField];
		if (!rawTime) continue;

		const timeValue = parseExcelValue(rawTime, "time");
		const regex = /^((0?[1-9])|(1[0-2])):[0-5]\d (AM|PM)$/;
		if (!regex.test(timeValue ?? "")) {
			pushError(
				"Row " +
					(rowIndex + ERROR_OFFSET) +
					": Invalid time format for " +
					timeField +
					".",
			);
		}
	}
};

export const filterHolidaysByCountry = (
	holidays: Holiday[] | undefined,
	country: string | undefined,
): Holiday[] => {
	if (!holidays) return [];

	const countryAbbr = country?.toUpperCase().trim();

	if (!countryAbbr) {
		return holidays.filter((h) => h.isTQL);
	}

	const isUSA = countryAbbr === "US" || countryAbbr === "USA";
	const isCAN = countryAbbr === "CA" || countryAbbr === "CAN";

	return holidays.filter((holiday) => {
		if (holiday.isTQL) return true;
		if (isUSA && holiday.isUSA) return true;
		return !!(isCAN && holiday.isCanadian);
	});
};

export const validateRowDate = (
	dateRaw: string,
	rowIndex: number,
	pushError: (msg: string) => void,
	holidays?: Holiday[],
) => {
	const dateVal = parseExcelValue(dateRaw, "date");
	const shippingDate = dateVal ? new Date(dateVal) : null;
	if (
		!shippingDate ||
		Number.isNaN(shippingDate.getTime()) ||
		shippingDate.getFullYear() > new Date().getFullYear() + 1
	) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Pickup Date not in valid excel Date format.",
		);
		return;
	}

	const now = new Date();
	now.setHours(0, 0, 0, 0);
	shippingDate.setHours(0, 0, 0, 0);
	if (shippingDate < now) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Pickup Date cannot be in the past.",
		);
	}
	if (shippingDate.getDay() === 6 || shippingDate.getDay() === 0) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Pickup Date cannot be on a weekend.",
		);
	}
	if (isHoliday(shippingDate, holidays)) {
		pushError(
			"Row " + (rowIndex + ERROR_OFFSET) + ": Pickup Date cannot be a holiday.",
		);
	}
};

export const validateRowZips = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
) => {
	const originZip = cleanZipCodeString(rowData["Origin Zip Code"]);
	const destZip = cleanZipCodeString(rowData["Destination Zip Code"]);
	if (originZip.length !== 5 && originZip.length !== 7) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Origin Zip Code must be five or six characters in length, depending on country.",
		);
	}
	if (destZip.length !== 5 && destZip.length !== 7) {
		pushError(
			"Row " +
				(rowIndex + ERROR_OFFSET) +
				": Destination Zip Code must be five or six characters in length, depending on country.",
		);
	}
};

export const findAccessorial = (
	name: string,
	data: PageData | null | undefined,
	stopType?: string,
) => {
	if (!data?.accessorials) return null;
	const allAccs = [
		...Object.values(data.accessorials.selectable).flat(),
		...Object.values(data.accessorials.autoApply).flat(),
		...Object.values(data.accessorials.locationType).flat(),
	];
	return allAccs.find((a) => {
		const matchesName = a.name.toLowerCase() === name.toLowerCase();
		if (stopType) {
			return (
				matchesName && a.stopType?.name.toLowerCase() === stopType.toLowerCase()
			);
		}
		return matchesName;
	});
};

type BulkUploadRowValidationOptions = {
	hasHazmatContactColumns: boolean;
	hasInsuranceColumns: boolean;
	holidays?: Holiday[];
	lookupPageData?: BulkUploadLookupData | null;
	templateType: BulkUploadTemplateType;
};

const validateSingleRow = (
	rowData: Record<string, string>,
	rowIndex: number,
	pushError: (msg: string) => void,
	options: BulkUploadRowValidationOptions,
) => {
	validateRowTimes(rowData, rowIndex, pushError);
	validateRowNmfc(rowData, rowIndex, pushError);
	validateRowDate(
		rowData["Pickup Date"] ?? "",
		rowIndex,
		pushError,
		filterHolidaysByCountry(options.holidays, rowData["Origin Country"]),
	);
	validateRowLocTypes(rowData, rowIndex, pushError);
	validateRowRequiredFields(rowData, rowIndex, pushError, options.templateType);
	validateRowZips(rowData, rowIndex, pushError);
	validateRowCaseCount(rowData, rowIndex, pushError);
	validateRowHazmatFlags(rowData, rowIndex, pushError);
	if (options.hasInsuranceColumns) {
		validateRowInsurance(rowData, rowIndex, pushError);
	}
	if (options.hasHazmatContactColumns) {
		validateRowHazmatContact(rowData, rowIndex, pushError);
	}
	if (options.templateType === "tender") {
		validateRowTenderHazmatDetails(rowData, rowIndex, pushError);
	}
	validateRowLookupValues(rowData, rowIndex, pushError, options.lookupPageData);
};

export const validateHeadersAndFields = (
	headers: string[],
	rows: RowValue[][],
	holidays?: Holiday[],
	templateTypeOrPageData:
		| BulkUploadTemplateType
		| BulkUploadLookupData
		| null = "quote",
	pageData?: BulkUploadLookupData | null,
	dataStartRow: number = ERROR_OFFSET,
): string[] => {
	const normalizedHeaders = normalizeBulkUploadHeaders(headers);
	const newErrors: string[] = [];
	const templateType =
		typeof templateTypeOrPageData === "string"
			? templateTypeOrPageData
			: "quote";
	const lookupPageData =
		typeof templateTypeOrPageData === "string"
			? pageData
			: templateTypeOrPageData;
	const requiredHeaders =
		templateType === "tender" ? TENDER_REQUIRED_HEADERS : REQUIRED_FIELDS;
	const missingHeaders = requiredHeaders.filter(
		(requiredHeader) => !normalizedHeaders.includes(requiredHeader),
	);
	// Insurance columns are optional, so older workbooks skip those checks.
	const hasInsuranceColumns = TENDER_INSURANCE_FIELDS.some((field) =>
		normalizedHeaders.includes(field),
	);

	if (missingHeaders.length > 0) {
		newErrors.push(
			"The following required column names are missing: " +
				missingHeaders.join(", "),
		);
		return newErrors;
	}

	let errorCount = 0;
	const pushError = (message: string) => {
		if (errorCount < MAX_ERROR_DISPLAY) {
			newErrors.push(message);
		}
		errorCount++;
	};

	for (const [rowIndex, row] of rows.entries()) {
		// Blank rows keep the numbering aligned with the worksheet but carry no data.
		if (isBlankBulkUploadRow(row)) continue;
		// Row validators apply the legacy offset internally, so normalize their
		// zero-based index to the workbook's resolved data start row.
		const validationRowIndex = rowIndex + dataStartRow - ERROR_OFFSET;

		const rowData: Record<string, string> = {};
		for (const [index, header] of normalizedHeaders.entries()) {
			rowData[header] = row[index]?.toString().trim() ?? "";
		}

		validateSingleRow(rowData, validationRowIndex, pushError, {
			hasHazmatContactColumns:
				normalizedHeaders.includes("Emergency Contact Name") ||
				normalizedHeaders.includes("Emergency Contact Phone"),
			hasInsuranceColumns,
			holidays,
			lookupPageData,
			templateType,
		});
	}

	if (errorCount > MAX_ERROR_DISPLAY) {
		newErrors.unshift(
			errorCount -
				MAX_ERROR_DISPLAY +
				" additional errors not shown. Fix the errors listed above, then re-upload to view the next set.",
		);
	}

	return newErrors;
};

const mapTenderHazmatDetails = (
	dataMap: Record<string, string>,
	pageData: PageData | null | undefined,
	item: number,
	isHazmat: boolean,
	templateType: BulkUploadTemplateType,
) => {
	if (templateType !== "tender") return {};
	if (!isHazmat) {
		return {
			emergencyContactName: null,
			emergencyContactPhone: null,
			unNumber: null,
			properShippingName: null,
			hazmatClassId: null,
			packingGroupId: null,
		};
	}

	return {
		emergencyContactName: dataMap["Emergency Contact Name"] || null,
		emergencyContactPhone: dataMap["Emergency Contact Phone"] || null,
		unNumber: dataMap[`Item${item} UN Number`] || null,
		properShippingName: dataMap[`Item${item} Proper Shipping Name`] || null,
		hazmatClassId: findLookupIdByNameOrCode(
			dataMap[`Item${item} Hazmat Class`] ?? "",
			pageData?.hazmatClasses ?? [],
		),
		packingGroupId: findLookupIdByNameOrCode(
			dataMap[`Item${item} Packing Group`] ?? "",
			pageData?.hazmatGroups ?? [],
		),
	};
};

export const mapRowCommodities = (
	dataMap: Record<string, string>,
	pageData: PageData | null | undefined,
	templateType: BulkUploadTemplateType = "quote",
) => {
	const commodities: BulkUploadQuoteCommodity[] = [];
	for (let i = 1; i <= MAX_SHIPMENT_ITEMS; i++) {
		const description = dataMap["Item" + i + " Description"];
		if (!description) continue;
		const isHazmat =
			(dataMap["Item" + i + " Is Hazmat"] ?? "").toLowerCase() === "y";
		const tenderHazmatDetails = mapTenderHazmatDetails(
			dataMap,
			pageData,
			i,
			isHazmat,
			templateType,
		);
		commodities.push({
			commodityId: 0,
			description,
			quantity:
				Number.parseInt(dataMap["Item" + i + " Quantity"] ?? "0", 10) || 0,
			unitTypeId:
				findLookupId(
					dataMap["Item" + i + " Unit Type"] ?? "",
					pageData?.unitTypes ?? [],
				) ?? 0,
			freightClassId:
				findLookupId(
					"Class " + (dataMap["Item" + i + " Freight Class"] ?? ""),
					pageData?.freightClassData ?? [],
				) ?? 0,
			weight: Math.ceil(
				Number.parseFloat(dataMap["Item" + i + " Weight"] ?? "0") || 0,
			),
			dimensionLength: Math.ceil(
				Number.parseFloat(dataMap["Item" + i + " Length"] ?? "0") || 0,
			),
			dimensionWidth: Math.ceil(
				Number.parseFloat(dataMap["Item" + i + " Width"] ?? "0") || 0,
			),
			dimensionHeight: Math.ceil(
				Number.parseFloat(dataMap["Item" + i + " Height"] ?? "0") || 0,
			),
			nmfc: dataMap["Item" + i + " NMFC"] ?? "",
			isHazmat,
			isStackable:
				(dataMap["Item" + i + " Is Stackable"] ?? "").toLowerCase() === "y",
			pieceCaseCount:
				Number.parseInt(dataMap["Item" + i + " Case Count"] ?? "0", 10) || null,
			...tenderHazmatDetails,
		});
	}
	return commodities;
};

export const getAccessorialKeyDetails = (
	key: string,
	pageData: PageData | null | undefined,
) => {
	const parts = key.split("-");
	const lookupKey = (parts[1] ?? key).trim();
	if (lookupKey === "Lift Gate") {
		return findAccessorial(
			lookupKey,
			pageData,
			key.includes("Pickup") ? "Pick" : "Drop",
		);
	}
	return findAccessorial(lookupKey, pageData);
};

export const mapRowAccessorials = (
	dataMap: Record<string, string>,
	originLocType: Accessorial | null | undefined,
	destLocType: Accessorial | null | undefined,
	pageData: PageData | null | undefined,
) => {
	const list: { id: number; description: string }[] = [];
	if (originLocType) {
		list.push({ id: originLocType.id, description: originLocType.name });
	}
	if (destLocType) {
		list.push({ id: destLocType.id, description: destLocType.name });
		if (destLocType.name.toLowerCase() === "residential") {
			list.push({ id: 35, description: "Appointment Delivery" });
		}
	}

	for (const key of SERVICE_KEYS) {
		if (dataMap[key] === "Y") {
			const acc = getAccessorialKeyDetails(key, pageData);
			if (acc) {
				list.push({ id: acc.id, description: acc.name });
			}
		}
	}

	return list.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
};

export const mapRowInsurance = (
	dataMap: Record<string, string>,
): Pick<
	BulkUploadQuote,
	| "insuranceApplyTypeId"
	| "insuranceProductTypeId"
	| "insuranceRequested"
	| "insuranceValue"
	| "productCondition"
> => {
	const isRequested =
		parseInsuranceRequested(dataMap[INSURANCE_REQUESTED_FIELD]) === true;
	const productTypeId = findInsuranceProductTypeId(
		dataMap[INSURANCE_PRODUCT_CONDITION_FIELD],
	);

	if (!isRequested) {
		return {
			insuranceApplyTypeId: null,
			insuranceProductTypeId: null,
			insuranceRequested: false,
			insuranceValue: null,
			productCondition: null,
		};
	}

	// Validation already rejected a requested row with a missing or invalid value
	// or condition, so anything mapped here carries a complete coverage request.
	return {
		insuranceApplyTypeId: INSURANCE_APPLY_TYPE_ALL_CARRIERS,
		insuranceProductTypeId: productTypeId,
		insuranceRequested: true,
		insuranceValue: parseInsuranceValue(dataMap[INSURANCE_VALUE_FIELD]),
		productCondition:
			INSURANCE_PRODUCT_CONDITIONS.find((item) => item.id === productTypeId)
				?.name ?? null,
	};
};

export const mapRowToQuote = (
	row: RowValue[],
	headers: string[],
	pageData: PageData | null | undefined,
	quoteState: { customer?: { id: number } },
	_isInternal: boolean,
	email: string,
	templateType: BulkUploadTemplateType = "quote",
): BulkUploadQuote => {
	const normalizedHeaders = normalizeBulkUploadHeaders(headers);
	const dataMap: Record<string, string> = {};
	for (const [index, header] of normalizedHeaders.entries()) {
		dataMap[header.trim()] = row[index]?.toString().trim() ?? "";
	}

	const originLocType = findAccessorial(
		dataMap["Shipper Location Type"] ?? "",
		pageData,
		"Pick",
	);
	const destLocType = findAccessorial(
		dataMap["Delivery Location Type"] ?? "",
		pageData,
		"Drop",
	);

	const quoteCommodities = mapRowCommodities(dataMap, pageData, templateType);
	const pieceCaseCount =
		quoteCommodities.reduce(
			(sum, item) => sum + (item.pieceCaseCount ?? 0),
			0,
		) || null;

	const tenderFields =
		templateType === "tender"
			? {
					emergencyContactName: dataMap["Emergency Contact Name"] || null,
					emergencyContactPhone: dataMap["Emergency Contact Phone"] || null,
				}
			: {};

	return {
		shipmentDate: parseExcelValue(dataMap["Pickup Date"], "date") ?? "",
		customerId: quoteState?.customer?.id ?? null,
		email,
		poNumbers: dataMap["PO Numbers"] ?? "",
		soNumbers: dataMap["SO Numbers"] ?? "",
		customerReferences: dataMap["Customer References"] ?? "",
		isVolume: false,
		origin: {
			postalCode: cleanZipCodeString(dataMap["Origin Zip Code"]),
			city: dataMap["Origin City"] ?? "",
			state: (dataMap["Origin State"] ?? "").toUpperCase(),
			country: dataMap["Origin Country"] ?? "",
			// locationType: originLocType,
			// locationServices: null,
		},
		destination: {
			postalCode: cleanZipCodeString(dataMap["Destination Zip Code"]),
			city: dataMap["Destination City"] ?? "",
			state: (dataMap["Destination State"] ?? "").toUpperCase(),
			country: dataMap["Destination Country"] ?? "",
			// locationType: destLocType,
			// locationServices: null,
		},
		pickupDetails: {
			stopName: dataMap["Shipper Name"] ?? "",
			address1: dataMap["Shipper Address1"] ?? "",
			address2: dataMap["Shipper Address2"] ?? "",
			contactName: dataMap["Shipper Contact Name"] ?? "",
			contactPhone: dataMap["Shipper Contact Phone"] || null,
			contactExtension: dataMap["Shipper Contact Ext"] || null,
			hoursOpen: parseExcelValue(dataMap["Shipper Start Hours"], "time"),
			hoursClose: parseExcelValue(dataMap["Shipper End Hours"], "time"),
			puNumber: dataMap["Pickup Number"] ?? "",
		},
		deliveryDetails: {
			stopName: dataMap["Receiver Name"] ?? "",
			address1: dataMap["Receiver Address1"] ?? "",
			address2: dataMap["Receiver Address2"] ?? "",
			contactName: dataMap["Receiver Contact Name"] ?? "",
			contactPhone: dataMap["Receiver Contact Phone"] || null,
			contactExtension: dataMap["Receiver Contact Ext"] || null,
			hoursOpen: parseExcelValue(dataMap["Receiver Start Hours"], "time"),
			hoursClose: parseExcelValue(dataMap["Receiver End Hours"], "time"),
			deliveryPO: dataMap["Delivery Number"] ?? "",
		},
		quoteCommodities,
		accessorials: mapRowAccessorials(
			dataMap,
			originLocType,
			destLocType,
			pageData,
		),
		// isGuest: false,
		pieceCaseCount,
		// carrierPrices: [],
		// quotePreviousRate: null,
		// selectedCarrierPriceId: null,
		...mapRowInsurance(dataMap),
		...tenderFields,
		// poNumber: null,
		// showBLNumber: false,
		specialInstructions:
			templateType === "tender"
				? dataMap["Special Instructions"] || null
				: null,
		// createdByDistributorId: null,
		templateId: null,
		// customer: {
		// 	id: 0,
		// 	name: "",
		// },
		// id: 0,
		// statusLevel: 1,
	};
};
